import React, { forwardRef } from 'react';
import { isEmpty } from 'lodash';

import { reportErrorInProductionOrThrow } from '~/Utils';
import cn from '~/Utils/cn';

import usePressRipple from '../../internal/usePressRipple';
import { useRadioGroup } from '../RadioGroup/context';

type RadioSize = 'xs' | 'medium';

export const SIZES = {
  xs: 'xs',
  medium: 'medium',
} as const satisfies Record<RadioSize, RadioSize>;

export interface RadioProps {
  /**
   * Whether the radio is checked. The radio is always controlled: a click reports through
   * `onChange` and the radio shows what `checked` says. Inside a RadioGroup the group's `value`
   * decides instead, and passing `checked` is an error.
   */
  checked?: boolean;
  /** The value this radio stands for. Inside a RadioGroup it is required: the group compares it with its own `value`. */
  value?: string;
  /**
   * Fired with the native input's change event when the radio becomes checked, by a click, the
   * Space key or an arrow key. A radio never reports becoming unchecked, so there is no checked
   * state to pass.
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
  /**
   * Radios that share a `name` form one group: the browser gives the group one Tab stop and moves
   * the selection with the arrow keys. Inside a RadioGroup the group supplies the name, and passing
   * `name` is an error.
   */
  name?: string;
  disabled?: boolean;
  /**
   * Declared because FormControlLabel (core/Atomic) clones its control with a `required` key on
   * every render, usually holding `undefined`, which the unexpected-props guard would otherwise
   * report. When set, it reaches the input.
   */
  required?: boolean;
  /** @default 'medium' */
  size?: RadioSize;
  className?: string;
  /** Applied to the input, so an external `<label htmlFor>` keeps working. */
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

// The glyph is 1em square, so the root's font size sets it: 15px (xs) or 24px (medium). With the
// root's 9px padding, the round hit area is 33px or 42px.
const glyphSizeClasses = {
  xs: 'text-[15px]',
  medium: 'text-[24px]',
} as const satisfies Record<RadioSize, string>;

// The Checkbox halo: a faint teal on hover in every state, and a stronger currentColor halo at 20%
// while the input has keyboard focus, blended by the browser because a build-time opacity modifier
// cannot apply to `currentColor`.
const haloInteractionClasses =
  'hover:bg-teal-700/[0.06] has-[:focus-visible]:bg-[color-mix(in_srgb,currentColor_20%,transparent)]';

// The dot scales in and out of the centre over 150ms, easing out as it appears and in as it
// leaves. `origin-center` is the middle of the 24-unit drawing, which is also the
// dot's own centre (an SVG element's default origin is the drawing's top-left corner).
const Glyph = ({ checked }: { checked: boolean }) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden className="block fill-current">
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle
      cx="12"
      cy="12"
      r="5"
      className={cn(
        'origin-center transition-transform duration-150 motion-reduce:transition-none',
        checked ? 'scale-100 ease-out' : 'scale-0 ease-in'
      )}
    />
  </svg>
);

/**
 * A radio built on the native `<input type="radio">`, styled with Tailwind tokens: grey when
 * unchecked, teal-700 when checked, in two sizes.
 *
 * Two modes, both supported:
 * - Inside a RadioGroup, the canonical API for a set of options: the group supplies the name, the
 *   checked value and the change handler.
 * - Standalone, with `checked` and `onChange` per radio, grouped by a shared `name`. This serves
 *   selectors whose radios sit in places one wrapping element cannot reach, such as one radio per
 *   table row, and the Formik layer, which renders one radio per option.
 *
 * Focus stays on the real input, which covers the whole round root. The glyph and the press ripple
 * sit in the root behind it, because an input cannot hold children.
 */
const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      checked,
      value,
      onChange,
      onClick,
      name,
      disabled,
      required,
      size = 'medium',
      className,
      id,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      ...restProps
    },
    ref
  ) => {
    const group = useRadioGroup();
    const ripple = usePressRipple();

    if (!isEmpty(restProps)) {
      reportErrorInProductionOrThrow(
        `Unexpected props passed to Radio: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the RadioProps interface and to the component's signature.`,
        true
      );
    }

    // Inside a group these would be silently overridden by the group's own, so they are reported.
    if (group && (checked !== undefined || name !== undefined || value === undefined)) {
      reportErrorInProductionOrThrow(
        'A Radio inside a RadioGroup takes its checked state and name from the group, and needs a value.',
        true
      );
    }

    const isChecked = group ? value !== undefined && group.value === value : Boolean(checked);
    const isDisabled = Boolean(disabled) || Boolean(group?.disabled);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(event);
      if (group && value !== undefined) {
        group.onChange(event, value);
      }
    };

    return (
      <span
        data-fs-component="radio"
        className={cn(
          ripple.rootClassName,
          'p-[9px]',
          'inline-flex shrink-0 select-none items-center justify-center rounded-full align-middle',
          'transition-[background-color] duration-150',
          glyphSizeClasses[size],
          isChecked ? 'text-teal-700' : 'text-slate-700',
          // A disabled radio is physically unreachable, so a click falls through to whatever is
          // behind it.
          isDisabled ? 'pointer-events-none cursor-default text-slate-600' : 'cursor-pointer',
          !isDisabled && haloInteractionClasses,
          className
        )}
        // The ripple listens on the root, which the input's own pointer events bubble to. It
        // guards itself here: no Base UI root swallows the press of a disabled radio.
        {...(isDisabled ? {} : ripple.getRootHandlers())}
      >
        <input
          ref={ref}
          type="radio"
          // Safari's default Tab skips native form controls, but not elements with an explicit
          // tabindex. Chrome, Firefox and Safari still apply their radio grouping on top of it:
          // one Tab stop per name, arrow keys inside it.
          tabIndex={0}
          className="absolute inset-0 z-[1] m-0 h-full w-full cursor-[inherit] rounded-full p-0 opacity-0"
          checked={isChecked}
          value={value}
          name={group ? group.name : name}
          onChange={handleChange}
          onClick={onClick}
          disabled={isDisabled}
          required={required}
          id={id}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
        />
        <Glyph checked={isChecked} />
        {ripple.rippleContainer}
      </span>
    );
  }
);

Radio.displayName = 'Radio';

export default Radio;
